import Section from '../../../components/Main/Section/Section';
import styled from 'styled-components';
import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ActionBar from '../../../components/ActionBar/ActionBar';
import { Add } from '@mui/icons-material';
import ContentContainer from '../../../components/Containers/ContentContainer';
import LeftContainer from '../../../components/Containers/LeftContainer';
import { AuthContext } from '../../../providers/AuthProvider';
import Guide from '../../../components/Guide/Guide';
import SurveyToolGuide from './SurveyToolGuide/SurveyToolGuide';
import NetPromoterScoreGuide from './NetPromoterScoreGuide/NetPromoterScoreGuide';
import { ISurveysEntry } from '../../../shared/interfaces/ISurveysEntry';
import Survey from '../../../components/Survey/Survey';
import NoContent from '../../../assets/images/monitoring_no_content.png';
import { CurrentViewContext } from '../../../providers/CurrentViewProvider';
import SurveyService from '../../../services/SurveyService';
import MoodBarometerGuide from './MoodBarometerGuide/MoodBarometerGuide';
import CustomSurveyGuide from './CustomSurveyGuide/CustomSurveyGuide';

const AddEditContainer = styled.div`
  margin-left: auto;
`;

const SurveyContainer = styled.div`
  margin-bottom: 0.5rem;
`;

const renderSurveyList = (
  surveyList: ISurveysEntry[],
  activeSurveyCount: number,
  totalSurveyCount: number,
  toggleArchive: (id: string) => void,
  reload: () => Promise<void>
) =>
  surveyList.map((survey) => (
    <SurveyContainer key={survey.id}>
      <Survey
        survey={survey}
        activeSurveyCount={activeSurveyCount}
        totalSurveyCount={totalSurveyCount}
        toggleArchive={toggleArchive}
        reload={reload}
      />
    </SurveyContainer>
  ));

const surveyFilter = (surveyList: ISurveysEntry[], filterCase: string) => {
  let sortedSurveys = [...surveyList];

  switch (filterCase) {
    case '1':
      return sortedSurveys.sort(
        (a, b) =>
          new Date(
            b.createdAt === '0001-01-01T00:00:00Z' || b.createdAt !== b.updatedAt
              ? b.updatedAt
              : b.createdAt
          ).getTime() -
          new Date(
            a.createdAt === '0001-01-01T00:00:00Z' || a.createdAt !== a.updatedAt
              ? a.updatedAt
              : a.createdAt
          ).getTime()
      );
    case '2':
      return sortedSurveys.sort((a, b) => a.label.localeCompare(b.label));
    case '3':
      return sortedSurveys.sort((a, b) => a.type.localeCompare(b.type));
    default:
      return sortedSurveys;
  }
};

const Surveys = () => {
  const { hasRole, exceedsLimit } = useContext(AuthContext);
  const { setCurrentView } = useContext(CurrentViewContext);
  const theme = useTheme();
  const { t } = useTranslation();
  const [sort, setSort] = useState('');
  const [filterBySurveys] = useState([]);
  const [kindOfSurvey, setKindOfSurvey] = useState<string>('');
  const [createdSurveys, setCreatedSurveys] = useState<ISurveysEntry[]>([]);
  const [archivedSurveys, setArchivedSurveys] = useState<ISurveysEntry[]>([]);
  const [filterByLabelName, setFilterByLabelName] = useState('');
  const [addSurvey, setAddSurvey] = useState<boolean>(false);
  const [surveyList, setSurveyList] = useState<ISurveysEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const getRecordCount = () => {
    if (filterBySurveys) {
      return `${surveyList.length} ${surveyList.length === 1 ? t('survey') : t('surveys')} `;
    } else if (surveyList.length === 0) {
      return ' ';
    }
  };

  const handleFilterChange = (event: SelectChangeEvent) => {
    setFilterByLabelName(event.target.value);
  };

  useEffect(() => {
    setCurrentView(t('survey_tool'));
    document.title = `${t('app_branding')} ${t('survey_tool')} `;
  }, [t]);

  useEffect(() => {
    let filteredAndSorted = surveyFilter(surveyList, sort);
    if (filterByLabelName) {
      filteredAndSorted = filteredAndSorted.filter((survey) => survey.label === filterByLabelName);
    }
    setCreatedSurveys(filteredAndSorted);
  }, [sort, surveyList, filterByLabelName]);

  const toggleArchive = async (id: string) => {
    const survey =
      surveyList.find((survey) => survey.id === id) ||
      archivedSurveys.find((survey) => survey.id === id);

    if (!survey) {
      console.log(`Umfrage mit der ID ${id} konnte nicht gefunden werden.`);
      return;
    }

    const newArchivedStatus = !survey.archived;

    try {
      await SurveyService.archivedSurvey(id, newArchivedStatus);

      if (newArchivedStatus) {
        setSurveyList((prevSurveys) => prevSurveys.filter((survey) => survey.id !== id));
        setArchivedSurveys((prevArchivedSurveys) => [
          ...prevArchivedSurveys,
          { ...survey, archived: true, archivedAt: new Date().toString() },
        ]);
      } else {
        setArchivedSurveys((prevArchivedSurveys) =>
          prevArchivedSurveys.filter((survey) => survey.id !== id)
        );
        setSurveyList((prevSurveys) => [
          ...prevSurveys,
          { ...survey, archived: false, updatedAt: new Date().toString() },
        ]);
      }
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Archivstatus:', error);
    }
  };

  const handleNavigate = () => {
    return setAddSurvey(true);
  };

  const reload = async () => {
    const controller = new AbortController();
    setAddSurvey(false);
    await getSurveys(controller.signal);
  };

  const getSurveys = async (signal: AbortSignal) => {
    const response = await SurveyService.getSurvey(setLoading, signal);

    if (!response) return;

    const archived = response.filter((survey: ISurveysEntry) => survey.archived);
    const notArchived = response.filter((survey: ISurveysEntry) => !survey.archived);

    setArchivedSurveys(archived);
    setSurveyList(notArchived);
  };

  useEffect(() => {
    const controller = new AbortController();

    (async () => {
      await getSurveys(controller.signal);
    })();

    return () => {
      controller.abort();
    };
  }, []);

  const activeSurveyCount = surveyList.length;
  const totalSurveyCount = surveyList.length + archivedSurveys.length;

  const renderedSurveys = renderSurveyList(
    createdSurveys,
    activeSurveyCount,
    totalSurveyCount,
    toggleArchive,
    reload
  );
  const renderedArchivedSurveys = renderSurveyList(
    archivedSurveys,
    activeSurveyCount,
    totalSurveyCount,
    toggleArchive,
    reload
  );
  const filteredSurveys = renderSurveyList(
    surveyList.filter((survey) => !filterByLabelName || survey.label === filterByLabelName),
    activeSurveyCount,
    totalSurveyCount,
    toggleArchive,
    reload
  );

  const uniqueCompanyNames = new Set(surveyList.map((survey) => survey.label));

  return hasRole('can-use-survey') ? (
    <Section>
      {totalSurveyCount === 0 || addSurvey ? (
        <Guide
          guideSelect={
            <>
              <img
                src={NoContent}
                alt="empty-state"
              />
              {!exceedsLimit('limitsurveys', totalSurveyCount + 1) && (
                <FormControl
                  size="small"
                  sx={{ mb: theme.spacing(4), minWidth: '17.75rem' }}
                >
                  <InputLabel id="select-label">{t('select_survey_type')}</InputLabel>
                  <Select
                    labelId="select-label"
                    label={t('select_survey_type')}
                    value={kindOfSurvey}
                    onChange={(e: SelectChangeEvent) => {
                      setKindOfSurvey(e.target.value);
                    }}
                  >
                    <MenuItem value="">
                      <em>---</em>
                    </MenuItem>
                    {hasRole('can-use-standard') && (
                      <MenuItem value={t('survey')}>{t('survey')}</MenuItem>
                    )}
                    <MenuItem value={t('nps_survey')}>{t('nps_survey')}</MenuItem>
                    {hasRole('can-use-mood') && (
                      <MenuItem value={t('mood_barometer')}>{t('mood_barometer')}</MenuItem>
                    )}
                    {hasRole('can-use-customsurvey') && (
                      <MenuItem value={t('custom_survey')}>{t('custom_survey')}</MenuItem>
                    )}
                  </Select>
                </FormControl>
              )}
            </>
          }
        >
          {kindOfSurvey === t('survey') && (
            <SurveyToolGuide
              reload={reload}
              hasAnswers={false}
            />
          )}
          {kindOfSurvey === t('nps_survey') && (
            <NetPromoterScoreGuide
              reload={reload}
              hasAnswers={false}
            />
          )}
          {kindOfSurvey === t('mood_barometer') && (
            <MoodBarometerGuide
              reload={reload}
              hasAnswers={false}
            />
          )}
          {kindOfSurvey === t('custom_survey') && (
            <CustomSurveyGuide
              reload={reload}
              hasAnswers={false}
            />
          )}
        </Guide>
      ) : (
        <ContentContainer>
          <LeftContainer>
            <ActionBar
              children={
                <AddEditContainer>
                  {!exceedsLimit('limitsurveys', totalSurveyCount + 1) && (
                    <Button
                      variant="contained"
                      startIcon={<Add />}
                      onClick={handleNavigate}
                    >
                      {t('create_survey')}
                    </Button>
                  )}
                </AddEditContainer>
              }
            />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                my: theme.spacing(2),
              }}
            >
              <Typography variant="subtitle2">{getRecordCount()}</Typography>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <FormControl
                  size="small"
                  sx={{ minWidth: '17.75rem' }}
                >
                  <InputLabel id="sort-select-label">{t('sort')}</InputLabel>
                  <Select
                    labelId="sort-select-label"
                    value={sort}
                    onChange={(event: SelectChangeEvent) => {
                      setSort(event.target.value);
                    }}
                    label={t('sort')}
                  >
                    <MenuItem value="">
                      <em>{t('unsorted')}</em>
                    </MenuItem>
                    <MenuItem value="1">{t('date')}</MenuItem>
                    <MenuItem value="2">{t('names')}</MenuItem>
                    <MenuItem value="3">{t('survey_type')}</MenuItem>
                  </Select>
                </FormControl>
                <FormControl
                  size="small"
                  sx={{ minWidth: '17.75rem', ml: theme.spacing(2) }}
                >
                  <InputLabel id="label-filter-label">{`${t('survey')} Label`}</InputLabel>
                  <Select
                    labelId="label-filter-label"
                    value={filterByLabelName}
                    onChange={handleFilterChange}
                    label={`${t('survey')} Label`}
                  >
                    <MenuItem value="">
                      <em>{t('unsorted')}</em>
                    </MenuItem>
                    {Array.from(uniqueCompanyNames).map((label: string) => (
                      <MenuItem
                        key={label}
                        value={label}
                      >
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>
            {filterByLabelName === '' ? (
              <SurveyContainer>
                {filterByLabelName === '' ? renderedSurveys : filteredSurveys}
              </SurveyContainer>
            ) : (
              <SurveyContainer>{filteredSurveys}</SurveyContainer>
            )}
            <Typography
              variant="subtitle2"
              sx={{ my: theme.spacing(2) }}
            >
              {`${t('archived_surveys')} (${archivedSurveys.length})`}
            </Typography>
            <SurveyContainer>{renderedArchivedSurveys}</SurveyContainer>
          </LeftContainer>
        </ContentContainer>
      )}
    </Section>
  ) : null;
};

export default Surveys;
